'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import type { SetScore } from '@/types/tournament.model';
import { SpecialResultType } from '@/types/tournament.model';

interface ConfirmResultModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  team1Name: string;
  team2Name: string;
  team1Score: number;
  team2Score: number;
  setScores: SetScore[];
  specialResultType?: SpecialResultType;
  onConfirm: () => void;
  isSubmitting: boolean;
}

export function ConfirmResultModal({
  open,
  onOpenChange,
  team1Name,
  team2Name,
  team1Score,
  team2Score,
  setScores,
  specialResultType = SpecialResultType.NONE,
  onConfirm,
  isSubmitting,
}: ConfirmResultModalProps) {
  const hasSpecialResult = specialResultType !== SpecialResultType.NONE;

  const getSpecialResultMessage = () => {
    switch (specialResultType) {
      case SpecialResultType.TEAM1_WALKOVER:
        return {
          title: 'Ausencia/Abandono',
          description: `${team1Name} gana por ausencia/abandono del rival`,
          winner: team1Name,
        };
      case SpecialResultType.TEAM2_WALKOVER:
        return {
          title: 'Ausencia/Abandono',
          description: `${team2Name} gana por ausencia/abandono del rival`,
          winner: team2Name,
        };
      case SpecialResultType.DOUBLE_WALKOVER:
        return {
          title: 'Doble walkover',
          description: 'Ambos equipos presentaron ausencia/abandono',
          winner: null,
        };
      default:
        return null;
    }
  };

  const specialResultInfo = getSpecialResultMessage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Confirmar resultado</DialogTitle>
          <DialogDescription>
            Por favor, verifica que el resultado ingresado sea correcto antes de
            guardar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Special Result Alert */}
          {hasSpecialResult && specialResultInfo && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <span className="font-semibold text-amber-800">
                  {specialResultInfo.title}
                </span>
              </div>
              <p className="mt-1 text-sm text-amber-700">
                {specialResultInfo.description}
              </p>
              {specialResultInfo.winner && (
                <p className="mt-2 text-sm font-medium text-amber-800">
                  Ganador: {specialResultInfo.winner}
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <h4 className="text-center font-medium">
              {hasSpecialResult
                ? 'Resultado en sets (informativo)'
                : 'Resultado final'}
            </h4>

            <div className="flex items-center justify-center gap-8 py-2">
              <div
                className={`text-center ${!hasSpecialResult && team1Score > team2Score ? 'font-bold text-green-600' : ''}`}
              >
                <div className="mb-1 text-sm">{team1Name}</div>
                <div className="text-3xl">{team1Score}</div>
                {!hasSpecialResult && team1Score > team2Score && (
                  <div className="mt-1 text-xs text-green-600">GANADOR</div>
                )}
              </div>

              <div className="text-xl font-light text-gray-400">vs</div>

              <div
                className={`text-center ${!hasSpecialResult && team2Score > team1Score ? 'font-bold text-green-600' : ''}`}
              >
                <div className="mb-1 text-sm">{team2Name}</div>
                <div className="text-3xl">{team2Score}</div>
                {!hasSpecialResult && team2Score > team1Score && (
                  <div className="mt-1 text-xs text-green-600">GANADOR</div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-center text-sm font-medium">Detalle de sets</h4>
            <div className="max-h-[30vh] space-y-1 overflow-y-auto pr-1">
              {setScores.map((set, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center gap-2 text-sm"
                >
                  <span className="w-6 text-right text-gray-500">
                    #{index + 1}
                  </span>
                  <span
                    className={`w-12 text-right ${!hasSpecialResult && set.team1Games > set.team2Games ? 'font-medium text-green-600' : ''}`}
                  >
                    {set.team1Games}
                  </span>
                  <span className="text-xs text-gray-500">-</span>
                  <span
                    className={`w-12 text-left ${!hasSpecialResult && set.team2Games > set.team1Games ? 'font-medium text-green-600' : ''}`}
                  >
                    {set.team2Games}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Volver a editar
          </Button>
          <Button onClick={onConfirm} disabled={isSubmitting}>
            {isSubmitting ? 'Guardando...' : 'Confirmar resultado'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
